import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/useAuth';
import { addBookmark, removeBookmark, subscriberQueries } from '@/lib/queries/subscriber';

export function BookmarkButton({ contentId }: { contentId: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const bookmarks = useQuery(subscriberQueries.bookmarks(user?.id));
  const [busy, setBusy] = useState(false);

  // Saving is a signed-in affordance; for anon the control hides rather than
  // failing silently on click (anon surfaces reach it via the public reader).
  if (!user) return null;

  const isBookmarked = (bookmarks.data ?? []).some((b) => b.content_id === contentId);

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    setBusy(true);
    try {
      if (isBookmarked) {
        await removeBookmark(user.id, contentId);
        toast.success('Removed from saved.');
      } else {
        await addBookmark(user.id, contentId);
        toast.success('Saved.');
      }
      await queryClient.invalidateQueries({ queryKey: ['bookmarks', user.id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update bookmark');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => void toggle(e)}
      disabled={busy}
      aria-label={isBookmarked ? 'Remove from saved' : 'Save for later'}
      aria-pressed={isBookmarked}
      className="rounded-md p-1.5 text-ink/50 hover:bg-ink/5 hover:text-oxblood disabled:opacity-60"
    >
      {isBookmarked ? (
        <BookmarkCheck className="h-4 w-4 text-oxblood" />
      ) : (
        <Bookmark className="h-4 w-4" />
      )}
    </button>
  );
}
